/**
 * Linked-mode team picker presentation. Renders a vertical list of
 * pressable rows; the row whose `isActive` flag is set shows a check icon.
 *
 * Loading / error / empty states are owned by the Container so this file
 * stays a pure single-expression arrow renderer.
 * @module features/team-selection/components/TeamSelectorLinked/TeamSelectorLinkedView
 */
import { CheckIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * One row's data, with its press handler pre-bound by the Container so
 * the View doesn't need to allocate functions during render.
 */
export interface TeamSelectorLinkedRow {
  readonly id: string;
  readonly name: string;
  readonly isActive: boolean;
  /** Pre-formatted "<League> · <flag> <Country>" line, or null when neither is known. */
  readonly subtitle: string | null;
  readonly onPress: () => void;
}

/**
 * Props for {@link TeamSelectorLinkedView}.
 */
export interface TeamSelectorLinkedViewProps {
  readonly rows: readonly TeamSelectorLinkedRow[];
}

/**
 * Linked-mode picker view.
 * @param root0 - Component props
 * @param root0.rows - Rows to render; each carries its own `onPress`
 * @returns The rendered linked-mode picker
 */
const TeamSelectorLinkedView = ({ rows }: TeamSelectorLinkedViewProps) => (
  <Stack space="1">
    {rows.map(row => (
      <Pressable
        key={row.id}
        testID={`team-selector-linked-option-${row.id}`}
        accessibilityRole="button"
        accessibilityLabel={row.name}
        accessibilityState={{ selected: row.isActive }}
        onPress={row.onPress}
      >
        <Stack
          direction="horizontal"
          className="items-center justify-between rounded-none border border-outline-default bg-surface-base p-3"
        >
          <Stack className="flex-1 gap-1">
            <Text
              variant="unstyled"
              className={
                row.isActive
                  ? "font-heading text-content-primary"
                  : "text-content-secondary"
              }
            >
              {row.name}
            </Text>
            {row.subtitle ? (
              <Text
                variant="caption"
                className="text-content-muted"
                numberOfLines={1}
              >
                {row.subtitle}
              </Text>
            ) : null}
          </Stack>
          {row.isActive ? (
            <Icon
              as={CheckIcon}
              className="text-needs-semantic-primary-500"
              size="sm"
            />
          ) : null}
        </Stack>
      </Pressable>
    ))}
  </Stack>
);

TeamSelectorLinkedView.displayName = "TeamSelectorLinkedView";

export default memo(TeamSelectorLinkedView);
